<script lang="ts">
	import { Dialog } from 'bits-ui';
	import { router } from '@inertiajs/svelte';
	import { toast } from 'svelte-sonner';
	import type { Lead } from '@/schemas/Lead';

	type Props = {
		open: boolean;
		lead: Lead | null;
		onClose: () => void;
	};

	let { open = $bindable(), lead, onClose }: Props = $props();

	function handleDelete() {
		if (!lead) return;

		router.delete(`/leads/${lead.id}`, {
			preserveScroll: true,
			onSuccess: () => {
				toast.success('Lead deleted successfully');
				onClose();
			},
			onError: () => {
				toast.error('Failed to delete lead');
			},
		});
	}
</script>

<Dialog.Root bind:open>
	<Dialog.Portal>
		<Dialog.Overlay
			class="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
		/>
		<Dialog.Content
			class="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
		>
			<Dialog.Title class="text-lg font-semibold leading-none tracking-tight">
				Confirm Delete
			</Dialog.Title>
			<Dialog.Description class="text-sm text-muted-foreground">
				Are you sure you want to delete this lead? This action cannot be undone.
			</Dialog.Description>

			{#if lead}
				<div class="rounded-lg border bg-muted/50 p-4">
					<div class="space-y-2 text-sm">
						<div>
							<span class="font-medium">Name:</span>
							{lead.first_name}
							{lead.last_name}
						</div>
						{#if lead.organisation_name}
							<div>
								<span class="font-medium">Organisation:</span>
								{lead.organisation_name}
							</div>
						{/if}
						{#if lead.email}
							<div>
								<span class="font-medium">Email:</span>
								{lead.email}
							</div>
						{/if}
					</div>
				</div>
			{/if}

			<div class="flex justify-end gap-3">
				<button
					type="button"
					onclick={onClose}
					class="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
				>
					Cancel
				</button>
				<button
					type="button"
					onclick={handleDelete}
					class="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground ring-offset-background transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
				>
					Delete Lead
				</button>
			</div>
		</Dialog.Content>
	</Dialog.Portal>
</Dialog.Root>